import { PRO_DATA_LAYER_LIMITS } from '../shared/data-layer.js';
import { portableDataLayerPayload } from './portable-data-layer.js';
import { HttpRequestError } from '../shared/request-error.js';
import { dataLayerStyleSchema, validateDataLayerPayload, type DataLayer } from '../shared/data-layer.js';
import { z } from 'zod';

const metadataSchema = z.object({ id: z.string(), name: z.string(), style: dataLayerStyleSchema, visible: z.boolean(), position: z.number(), version: z.number().nullable().optional() }).loose();
const listResponseSchema = z.object({ layers: z.array(metadataSchema) });
const createResponseSchema = z.object({ layer: z.object({ id: z.string() }).loose(), uploadUrl: z.string() });

function layerUrl(mapId: string, layerId?: string): string {
  const root = `/api/maps/${encodeURIComponent(mapId)}/layers`;
  return layerId ? `${root}/${encodeURIComponent(layerId)}` : root;
}

async function responseJson<Output>(response: Response, schema: z.ZodType<Output>): Promise<Output> {
  return schema.parse(await response.json());
}

export async function loadMapDataLayers(mapId: string, fetchImpl: typeof fetch = fetch): Promise<{ layers: DataLayer[]; failed: number }> {
  const response = await fetchImpl(layerUrl(mapId), { credentials: 'same-origin' });
  if (!response.ok) throw new HttpRequestError(`Data layer list failed: ${response.status}`, response, 'data-layer-client');
  const metadata = (await responseJson(response, listResponseSchema)).layers;
  const layers: DataLayer[] = [];
  let failed = 0;
  for (const item of metadata) {
    const id = item.id;
    try {
      const payloadResponse = await fetchImpl(layerUrl(mapId, id), { credentials: 'same-origin' });
      if (!payloadResponse.ok) throw new HttpRequestError(String(payloadResponse.status), payloadResponse, 'data_layer.download');
      const validation = validateDataLayerPayload(await payloadResponse.json(), PRO_DATA_LAYER_LIMITS);
      if (!validation.success) throw new Error(validation.errors.join(' '));
      layers.push({
        ...validation.payload.layer,
        id,
        name: item.name,
        style: item.style,
        visible: item.visible,
        position: item.position,
        featureCount: validation.payload.features.length,
        bounds: validation.payload.layer.bounds,
        saved: true,
        persisted: true,
        serverVersion: item.version ?? undefined,
        features: validation.payload.features,
      });
    } catch (error) { console.error('Data layer download failed:', error); failed += 1; }
  }
  return { layers, failed };
}

export async function saveMapDataLayers(mapId: string, layers: DataLayer[], fetchImpl: typeof fetch = fetch, pro = false): Promise<{ saved: number; failed: number }> {
  let saved = 0;
  let failed = 0;
  for (const layer of layers.filter((candidate) => !candidate.saved)) {
    try {
      const payload = await portableDataLayerPayload(layer, pro);
      if (!layer.persisted) {
        const createResponse = await fetchImpl(layerUrl(mapId), {
          method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ clientId: layer.id, name: layer.name, sourceFormat: layer.sourceFormat, style: layer.style, visible: layer.visible }),
        });
        if (!createResponse.ok) throw new HttpRequestError(`Layer create failed: ${createResponse.status}`, createResponse, 'data-layer-client');
        const created = await responseJson(createResponse, createResponseSchema);
        layer.id = created.layer.id;
        layer.persisted = true;
      }
      const uploaded = await fetchImpl(`${layerUrl(mapId, layer.id)}/upload`, {
        method: 'PUT', credentials: 'same-origin', headers: { 'content-type': 'application/json', 'if-match': String(layer.serverVersion ?? 0) }, body: JSON.stringify({ ...payload, layer: { ...payload.layer, id: layer.id } }),
      });
      if (!uploaded.ok) throw new HttpRequestError(`Layer upload failed: ${uploaded.status}`, uploaded, 'data-layer-client');
      const version = z.object({ version: z.number().optional() }).parse(await uploaded.json());
      layer.serverVersion = version.version;
      layer.saved = true;
      saved += 1;
    } catch (error) {
      console.error('Data layer save failed:', error);
      failed += 1;
    }
  }
  return { saved, failed };
}

export async function deleteMapDataLayer(mapId: string, layerId: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  const response = await fetchImpl(layerUrl(mapId, layerId), { method: 'DELETE', credentials: 'same-origin' });
  if (!response.ok) throw new HttpRequestError(`Layer delete failed: ${response.status}`, response, 'data-layer-client');
}
